// frontend/packages/business-console/src/pages/Invoices/useInvoiceDetail.js
//
// Data hook for the Invoice detail page. Mirrors useQuoteDetail with
// an extra parallel fetch for the CrmPayment rows that drive the
// payment-status panel (design doc §3.6).
//
//   - Demo: pulls from demoInvoiceDetail.js fixtures.
//   - Real: parallel-fetches invoice + customer + payments via
//     getInvoice() + getCustomer() + listInvoicePayments().

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getInvoice, listInvoicePayments } from '../../services/invoiceService';
import { getCustomer } from '../../services/crmService';
import { getDemoInvoiceDetail } from './demoInvoiceDetail';

function readDemoFlag(searchString) {
  const params = new URLSearchParams(searchString || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

export function useInvoiceDetail(id) {
  const location = useLocation();
  const isDemo = useMemo(
    () => readDemoFlag(location.search),
    [location.search]
  );

  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState([]);
  const [availableActions, setAvailableActions] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      if (isDemo) {
        const fixture = getDemoInvoiceDetail(id);
        if (!fixture) {
          setError(new Error('Demo invoice not found'));
          return;
        }
        setInvoice(fixture.invoice);
        setCustomer(fixture.customer);
        setPayments(fixture.payments || []);
        setAvailableActions(fixture.availableActions);
        setBusiness(fixture.business || null);
      } else {
        const res = await getInvoice(id);
        setInvoice(res.invoice);
        setAvailableActions(res.availableActions);
        // Customer + payments in parallel; both are non-fatal.
        const [cust, paymentsRes] = await Promise.all([
          getCustomer(res.invoice.customerId).catch(() => null),
          listInvoicePayments(res.invoice._id).catch(() => ({ items: [] })),
        ]);
        // getCustomer returns { customer, activities, person, ... } — unwrap
        // (matches the editors). Otherwise the document header falls back to
        // "BILL TO: Customer".
        setCustomer(cust?.customer || cust || null);
        setPayments(paymentsRes?.items || []);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id, isDemo]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    invoice,
    customer,
    payments,
    availableActions,
    business,
    loading,
    error,
    refresh: load,
    isDemo,
  };
}
